/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/camelcase */
import Vue from 'vue'

import {
  getAccessToken,
  getRefreshToken,
  removeAccessToken,
  removeRefreshToken,
  setRefreshToken,
} from '@/utils/auth'

import { CreateOrUpdate as saveDefault } from '@/api/default'
import { GetInfo, Login, Logout, RefreshToken } from '@/api/entrance'

import router, { resetRouter } from '@/router'

import { UserDefaultSetting } from '@/types/user'
import { IUserState, Module, RootState } from '../types'

// seconds
const refreshTokenInterval = process.env.VUE_APP_TOKEN_REFRESH_INTERVAL || 3600
let continuousRefreshTokenRunning = false

export const user: Module<IUserState, RootState> = {
  namespaced: true,
  state() {
    return {
      id: -1,
      token: getAccessToken(),
      name: '',
      avatar: '',
      introduction: '',
      roles: [],
      default: {},
    }
  },

  mutations: {
    SET_ID: (state: IUserState, id: number) => {
      state.id = id
    },
    SET_INTRODUCTION: (state: IUserState, introduction: string) => {
      state.introduction = introduction
    },
    SET_NAME: (state: IUserState, name: string) => {
      state.name = name
    },
    SET_AVATAR: (state: IUserState, avatar: string) => {
      state.avatar = avatar
    },
    SET_ROLES: (state: IUserState, roles: Array<string>) => {
      state.roles = roles
    },
    SET_DEFAULT: (state: IUserState, def: UserDefaultSetting) => {
      state.default = def
    },
  },

  actions: {
    // user login
    async login({ dispatch }, credentials) {
      const { username, password } = credentials

      try {
        const { refresh_token } = await Login(username.trim(), password, false)
        setRefreshToken(refresh_token)
        // eslint-disable-next-line
        ;(Vue as any).$toast.success('Success, you has been logged in.')

        // Check redirect request
        if (typeof router.currentRoute.query.r === 'string') {
          location.href = router.currentRoute.query.r
        } else {
          location.href = '/'
          // router.push({ path: '/' })
        }
        dispatch('continuousRefreshToken')
      } catch {
        // eslint-disable-next-line
        // ;(Vue as any).$toast.error('Username and password  is invalid!')
      }
    },

    async refreshToken(): Promise<boolean> {
      const token = getRefreshToken()
      if (typeof token == 'string' && token != '') {
        const { data, code } = await RefreshToken(token)
        // eslint-disable-next-line
        if (typeof data == 'object' && code === 20201) {
          setRefreshToken(data.refresh_token)
          return true
        }
      }
      return false
    },

    async continuousRefreshToken({ dispatch }) {
      if (continuousRefreshTokenRunning) {
        return
      }

      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        return
      }

      continuousRefreshTokenRunning = true
      setTimeout(async () => {
        await dispatch('refreshToken')
        continuousRefreshTokenRunning = false
        dispatch('continuousRefreshToken')
      }, refreshTokenInterval * 1000)
    },

    // get user info
    getInfo({ commit }) {
      return new Promise((resolve, reject) => {
        GetInfo()
          // eslint-disable-next-line
          .then((data) => {
            const { id, roles, name, avatar, introduction, defaults } = data

            // roles must be a non-empty array
            if (!roles || roles.length <= 0) {
              reject('getInfo: roles must be a non-null array!')
            }

            commit('SET_ID', id)
            commit('SET_ROLES', roles)
            commit('SET_NAME', name)
            commit('SET_AVATAR', avatar)
            commit('SET_INTRODUCTION', introduction)
            commit('SET_DEFAULT', defaults)
            resolve(data)
          })
          // eslint-disable-next-line
          .catch((error: any) => {
            reject(error)
          })
      })
    },

    // user logout
    logout({ commit }) {
      return new Promise((resolve, reject) => {
        Logout()
          .then(() => {
            commit('SET_ROLES', [])
            removeAccessToken()
            removeRefreshToken()
            resetRouter()
            // eslint-disable-next-line
            ;(Vue as any).$toast.success('Success, You have logged out.')

            const currentPath = router.currentRoute.path
            router.push({
              path: '/login',
              query: { r: currentPath },
              replace: true,
            })
            resolve(true)
          })
          // eslint-disable-next-line
          .catch((error: any) => {
            reject(error)
          })
      })
    },

    // remove token
    resetToken({ commit }) {
      return new Promise((resolve) => {
        commit('SET_ROLES', [])
        removeAccessToken()
        removeRefreshToken()
        resolve(true)
      })
    },

    // dynamically modify permissions
    async changeRoles({ dispatch }) {
      const { roles } = await dispatch('getInfo')

      resetRouter()

      // generate accessible routes map based on roles
      const accessRoutes = await dispatch('permission/generateRoutes', roles, {
        root: true,
      })

      // dynamically add accessible routes
      router.addRoutes(accessRoutes)

      // reset visited views and cached views
      dispatch('tagsView/delAllViews', null, { root: true })
    },

    // eslint-disable-next-line
    async updateDefault({ state }, def: any) {
      await saveDefault({ ...state.default, ...def })
    },
  },
}
